// Universal document-detail overlay context.
//
// Mount `DocumentDetailOverlayProvider` once near the case-shell root. Any
// descendant (Documents row, Graph entity-detail "Source document" link, Map,
// Insights, AI Agent source links) calls `useDocumentOverlay().open(documentId)`
// to open the overlay above the current workspace. Closing returns the user
// to exactly where they were — no route change, no unmount.
//
// The open document lives in the `?doc=<id>` URL search param — the URL is the
// single source of truth. That makes every open document a shareable /
// refreshable deep link on ANY case page, and lets the browser Back button
// close the overlay. Landing on a case route with `?doc=<id>` opens the overlay
// automatically.

import { type ReactNode, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import {
  clearFocusParams,
  FACE_PAGE_PARAM,
  FACE_PARAM,
  FOCUS_PERSON_PARAM,
  focusFromParams,
  PLAY_PARAM,
  TIME_END_PARAM,
  TIME_PARAM,
  writeFocusParams,
} from '@/features/document-overlay/focus'
import { DocumentDetailOverlay } from './DocumentDetailOverlay'
import {
  DocumentOverlayContext,
  type DocumentOverlayOptions,
  type DocumentOverlayState,
  type DocumentOverlayTarget,
} from './overlay-context'

/** URL search param that holds the open document id. */
export const DOC_PARAM = 'doc'

// The context and its hooks live next door, so opening a document costs no
// preview stack; both stay exported from here for the callers that always used
// this module.
export {
  type DocumentOverlayOptions,
  type DocumentOverlayTarget,
  useDocumentOverlay,
  useDocumentOverlayOptional,
} from './overlay-context'

/** Both call shapes as one target, so only the URL writer has to know them. */
function asTarget(
  target: string | DocumentOverlayTarget,
  options?: DocumentOverlayOptions
): DocumentOverlayTarget {
  return typeof target === 'string' ? { documentId: target, faceId: options?.faceId } : target
}

export function DocumentDetailOverlayProvider({
  caseId,
  children,
}: {
  caseId: string
  children: ReactNode
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const documentId = searchParams.get(DOC_PARAM)
  // Memoised on the raw params: the focus is an object, and a new one on every
  // render would churn the context value and re-render every consumer.
  const face = searchParams.get(FACE_PARAM)
  const facePage = searchParams.get(FACE_PAGE_PARAM)
  const focusPerson = searchParams.get(FOCUS_PERSON_PARAM)
  const play = searchParams.get(PLAY_PARAM)
  const t = searchParams.get(TIME_PARAM)
  const tEnd = searchParams.get(TIME_END_PARAM)
  const focus = useMemo(
    () => focusFromParams({ face, facePage, focusPerson, play, t, tEnd }),
    [face, facePage, focusPerson, play, t, tEnd]
  )

  // react-router recreates `setSearchParams` (and `searchParams`) on every URL
  // change, so keep the live bindings in refs. That lets `open`/`close` stay
  // referentially stable (empty deps) — otherwise the context value would churn
  // on every unrelated param change and re-render every consumer, including the
  // per-row overlay hooks behind React.memo.
  const setSearchParamsRef = useRef(setSearchParams)
  setSearchParamsRef.current = setSearchParams
  const isOpenRef = useRef(documentId !== null)
  isOpenRef.current = documentId !== null

  // The focus travels in the URL next to the document, so a photo opened from
  // the identity page stays a shareable deep link — and a plain open() from
  // search, the agent or the documents list clears it, keeping that path
  // exactly as it was.
  const open = useCallback(
    (target: string | DocumentOverlayTarget, options?: DocumentOverlayOptions) => {
      const wanted = asTarget(target, options)
      setSearchParamsRef.current(
        prev => {
          const next = new URLSearchParams(prev)
          next.set(DOC_PARAM, wanted.documentId)
          writeFocusParams(next, wanted)
          return next
        },
        // Opening from a page with no document pushes a history entry so Back
        // closes the overlay and returns the user to exactly where they were.
        // Switching between documents replaces it so Back never has to step
        // through a chain of overlays.
        { replace: isOpenRef.current }
      )
    },
    []
  )

  const close = useCallback(() => {
    setSearchParamsRef.current(
      prev => {
        const next = new URLSearchParams(prev)
        next.delete(DOC_PARAM)
        clearFocusParams(next)
        return next
      },
      { replace: true }
    )
  }, [])

  const value = useMemo<DocumentOverlayState>(
    () => ({ isOpen: documentId !== null, documentId, focus, open, close }),
    [documentId, focus, open, close]
  )

  return (
    <DocumentOverlayContext.Provider value={value}>
      {children}
      {documentId !== null &&
        createPortal(
          <DocumentDetailOverlay
            caseId={caseId}
            documentId={documentId}
            focus={focus}
            onClose={close}
          />,
          document.body
        )}
    </DocumentOverlayContext.Provider>
  )
}
